/**
 * AppNav — the sidebar down the left of the RMS's pages
 * (DESIGN-SYSTEM.md, Admin global navigation).
 *
 * Laid out as intake's is, and changing with the page rather than sitting
 * beside it:
 *
 * - **Its head** is the one search (`SearchDialog`, also ⌘K) and the way
 *   into Settings, together, because both are about the whole workspace
 *   rather than the page in front of you.
 * - **The places**: Events, Schedule and Equipment. Nothing rests filled
 *   to say which is open: the page's own title says it. `aria-current`
 *   still tells a screen reader.
 * - **The account** at the foot: its email, your Profile, and Log out.
 *
 * The RMS has no page views under the places: Events' filters are its
 * filter row (DESIGN.md, Admin global navigation).
 *
 * While Settings is open the sidebar is Settings: the way back at its top
 * and the sections under it, the open one filled, then the link out to
 * intake's settings.
 *
 * The account is its own email because one person may administer more
 * than one Company across the two apps, and the address is the only thing
 * on either screen that says which.
 *
 * The account menu is hand-rolled rather than Astryx's `DropdownMenu`,
 * whose popover positions itself with CSS anchor positioning, which
 * Safari on iOS ignores. This is the house popup: a `relative` wrapper
 * with an absolutely positioned panel.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ExternalLinkIcon,
  GearIcon,
  SearchIcon,
} from "./icons";
import {
  DESTINATIONS,
  isActiveDestination,
  type Destination,
} from "./destinations";
import { useOptionalAuth } from "../../lib/auth";
import { useDismissable } from "../../lib/use-dismissable";
import { RAIL, useMedia } from "../../lib/use-media";
import { isSettings, lastPlace } from "../../lib/last-place";
import {
  INTAKE_SETTINGS_URL,
  SETTINGS_GROUPS,
  SETTINGS_SECTIONS,
  settingsPath,
} from "../../lib/settings-index";
import { SearchDialog } from "./SearchDialog";
import { Tooltip } from "./Tooltip";
import styles from "./AppNav.module.css";

/** In the rail an icon is all there is, so it is named on hover. */
function Named({
  name,
  rail,
  children,
}: {
  name: string;
  rail: boolean;
  children: ReactNode;
}) {
  return rail ? (
    <Tooltip content={name} trigger="icon" side="right" className={styles.named}>
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  );
}

function NavItem({
  d,
  pathname,
  rail,
}: {
  d: Destination;
  pathname: string;
  rail: boolean;
}) {
  return (
    <li>
      <Named name={d.label} rail={rail}>
        <Link
          to={d.to}
          className={styles.item}
          aria-current={isActiveDestination(pathname, d.to) ? "page" : undefined}
        >
          <span className={styles.itemIcon}>{d.icon}</span>
          <span className={styles.itemLabel}>{d.label}</span>
        </Link>
      </Named>
    </li>
  );
}

/**
 * The head of the sidebar: the one search and Settings. Icons alone; a
 * magnifier and a gear need no tooltip to say so.
 */
function Head({ onSearch }: { onSearch: () => void }) {
  return (
    <div className={styles.head}>
      <button
        type="button"
        aria-label="Search"
        onClick={onSearch}
        className={styles.headButton}
      >
        <SearchIcon />
      </button>
      <Link to="/settings" aria-label="Settings" className={styles.headButton}>
        <GearIcon />
      </Link>
    </div>
  );
}

/** While Settings is open, the sidebar is its section list. */
function SettingsNav() {
  const [params] = useSearchParams();
  const open = params.get("section") ?? "general";
  return (
    <div className={styles.settings}>
      <Link to={lastPlace()} className={styles.back}>
        <ChevronLeftIcon className={styles.backIcon} />
        Back
      </Link>
      {SETTINGS_GROUPS.map((g) => (
        <div key={g.id} className={styles.views}>
          <h2 className={styles.viewsLabel}>{g.label}</h2>
          <ul className={styles.viewList}>
            {SETTINGS_SECTIONS.filter((s) => s.group === g.id).map(
              ({ id, label }) => (
                <li key={id}>
                  <Link
                    to={settingsPath(id)}
                    replace
                    aria-current={open === id ? "page" : undefined}
                    className={open === id ? styles.sectionOn : styles.section}
                  >
                    {label}
                  </Link>
                </li>
              ),
            )}
            {g.id === "company" && (
              <li>
                <a
                  href={INTAKE_SETTINGS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.external}
                >
                  Intake settings
                  <ExternalLinkIcon className={styles.externalIcon} />
                </a>
              </li>
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** The account at the foot: its email, and behind it your Profile and Log out. */
function AccountMenu({ rail }: { rail: boolean }) {
  const auth = useOptionalAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    onEscape: () => {
      setOpen(false);
      triggerRef.current?.focus();
    },
    pointer: [containerRef],
  });

  const email = auth?.user?.email ?? "Account";

  return (
    <div ref={containerRef} className={styles.account}>
      <Named name={email} rail={rail && !open}>
        <button
          ref={triggerRef}
          type="button"
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((o) => !o)}
          className={styles.accountTrigger}
        >
          <span className={styles.accountInitial} aria-hidden="true">
            {email.charAt(0).toUpperCase()}
          </span>
          <span className={styles.accountEmail}>{email}</span>
          <ChevronDownIcon className={styles.accountChevron} />
        </button>
      </Named>
      {open && (
        <div role="menu" aria-label="Account" className={styles.menu}>
          <p className={styles.menuEmail}>{email}</p>
          <Link
            role="menuitem"
            to={settingsPath("profile")}
            className={styles.menuItem}
            onClick={() => setOpen(false)}
          >
            Profile
          </Link>
          <hr className={styles.menuRule} />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void (async () => {
                await auth?.signOut();
                window.location.assign("/login");
              })();
            }}
            className={styles.menuItem}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

export function AppNav() {
  const { pathname } = useLocation();
  const settings = isSettings(pathname);
  const rail = useMedia(RAIL) && !settings;
  const [searching, setSearching] = useState(false);

  // ⌘K / Ctrl+K opens the search from anywhere, as it does in most apps
  // with one.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearching(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <nav
      aria-label="Main"
      className={settings ? styles.navSettings : styles.nav}
    >
      <Head onSearch={() => setSearching(true)} />
      {settings ? (
        <SettingsNav />
      ) : (
        <ul className={styles.places}>
          {DESTINATIONS.map((d) => (
            <NavItem key={d.to} d={d} pathname={pathname} rail={rail} />
          ))}
        </ul>
      )}
      <div className={styles.foot}>
        <AccountMenu rail={rail} />
      </div>
      <SearchDialog open={searching} onClose={() => setSearching(false)} />
    </nav>
  );
}
